import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Megaphone,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Tag,
  Eye,
  EyeOff,
  Sparkles,
  Clock,
  AlertCircle,
  Check,
  X,
  Gift,
  Percent,
  Star
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const bulletinTypes = [
  { id: "announcement", label: "Announcement", icon: Megaphone, color: "bg-blue-500" },
  { id: "sale", label: "Sale/Promotion", icon: Percent, color: "bg-green-500" },
  { id: "holiday", label: "Holiday Hours", icon: Calendar, color: "bg-amber-500" },
  { id: "special", label: "Special Offer", icon: Gift, color: "bg-purple-500" }
];

/**
 * BulletinForm - Form component for creating/editing bulletins
 */
const BulletinForm = ({ data, setData, onSubmit, onCancel, saving, isEdit = false }) => (
  <div className="space-y-4">
    <div>
      <Label className="text-zinc-300">Bulletin Type</Label>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {bulletinTypes.map(type => (
          <button
            key={type.id}
            onClick={() => setData(prev => ({ ...prev, type: type.id }))}
            className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
              data.type === type.id 
                ? 'border-amber-500 bg-amber-500/20' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg ${type.color} flex items-center justify-center`}>
              <type.icon className="w-4 h-4 text-white" />
            </div>
            <span className="text-white text-sm">{type.label}</span>
          </button>
        ))}
      </div>
    </div>

    <div>
      <Label className="text-zinc-300">Title *</Label>
      <Input
        value={data.title}
        onChange={(e) => setData(prev => ({ ...prev, title: e.target.value }))}
        placeholder="e.g., Summer Sale! 20% Off All Services"
        className="bg-zinc-800 border-zinc-700"
        maxLength={100}
      />
      <p className="text-xs text-zinc-500 mt-1">{data.title.length}/100 characters</p>
    </div>

    <div>
      <Label className="text-zinc-300">Message *</Label>
      <Textarea
        value={data.message}
        onChange={(e) => setData(prev => ({ ...prev, message: e.target.value }))}
        placeholder="Share details about your announcement, sale, or special offer..."
        className="bg-zinc-800 border-zinc-700 min-h-[100px]"
        maxLength={500}
      />
      <p className="text-xs text-zinc-500 mt-1">{data.message.length}/500 characters</p>
    </div>

    {(data.type === "sale" || data.type === "special") && (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Discount Code (optional)</Label>
          <Input
            value={data.discount_code}
            onChange={(e) => setData(prev => ({ ...prev, discount_code: e.target.value.toUpperCase() }))}
            placeholder="SUMMER20"
            className="bg-zinc-800 border-zinc-700"
            maxLength={20}
          />
        </div>
        <div>
          <Label className="text-zinc-300">Discount % (optional)</Label>
          <Input
            type="number"
            value={data.discount_percent}
            onChange={(e) => setData(prev => ({ ...prev, discount_percent: e.target.value }))}
            placeholder="20"
            className="bg-zinc-800 border-zinc-700"
            min="0"
            max="100"
          />
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-zinc-300">Start Date (optional)</Label>
        <Input
          type="date"
          value={data.start_date}
          onChange={(e) => setData(prev => ({ ...prev, start_date: e.target.value }))}
          className="bg-zinc-800 border-zinc-700"
        />
      </div>
      <div>
        <Label className="text-zinc-300">End Date (optional)</Label>
        <Input
          type="date"
          value={data.end_date}
          onChange={(e) => setData(prev => ({ ...prev, end_date: e.target.value }))}
          className="bg-zinc-800 border-zinc-700"
        />
      </div>
    </div>

    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
      <div className="flex items-center gap-2">
        <Star className="w-4 h-4 text-amber-400" />
        <span className="text-zinc-300">Pin to top of Mini-Site</span>
      </div>
      <Switch
        checked={data.is_pinned}
        onCheckedChange={(checked) => setData(prev => ({ ...prev, is_pinned: checked }))}
      />
    </div>

    <DialogFooter>
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button 
        onClick={onSubmit}
        disabled={saving || !data.title || !data.message}
        className="bg-amber-500 hover:bg-amber-600 text-black"
      >
        {saving ? "Saving..." : isEdit ? "Update Bulletin" : "Create Bulletin"}
      </Button>
    </DialogFooter>
  </div>
);

/**
 * BulletinBoard - Allows stylists to post announcements visible to clients when booking
 */
const BulletinBoard = ({ token, membershipTier = "free" }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingBulletin, setEditingBulletin] = useState(null);
  
  const [newBulletin, setNewBulletin] = useState({
    title: "",
    message: "",
    type: "announcement", // announcement, sale, holiday, special
    discount_code: "",
    discount_percent: "",
    start_date: "",
    end_date: "",
    is_active: true,
    is_pinned: false
  });

  const isPro = membershipTier === "pro" || membershipTier === "elite";
  const maxBulletins = isPro ? 10 : 2;

  useEffect(() => {
    fetchBulletins();
  }, [token]);

  const fetchBulletins = async () => {
    try {
      const response = await axios.get(`${API}/bulletins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins(response.data.bulletins || []);
    } catch (err) {
      console.error("Failed to fetch bulletins:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newBulletin.title || !newBulletin.message) {
      toast.error("Please fill in title and message");
      return;
    }

    if (bulletins.length >= maxBulletins) {
      toast.error(`Maximum ${maxBulletins} bulletins allowed. ${!isPro ? 'Upgrade to Pro for more!' : ''}`);
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/bulletins`, newBulletin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins([response.data.bulletin, ...bulletins]);
      setShowCreateDialog(false);
      setNewBulletin({
        title: "",
        message: "",
        type: "announcement",
        discount_code: "",
        discount_percent: "",
        start_date: "",
        end_date: "",
        is_active: true,
        is_pinned: false
      });
      toast.success("Bulletin created successfully!");
    } catch (err) {
      toast.error("Failed to create bulletin");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingBulletin) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/bulletins/${editingBulletin.id}`, editingBulletin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins(bulletins.map(b => b.id === editingBulletin.id ? editingBulletin : b));
      setEditingBulletin(null);
      toast.success("Bulletin updated!");
    } catch (err) {
      toast.error("Failed to update bulletin");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bulletinId) => {
    if (!confirm("Delete this bulletin?")) return;
    
    try {
      await axios.delete(`${API}/bulletins/${bulletinId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins(bulletins.filter(b => b.id !== bulletinId));
      toast.success("Bulletin deleted");
    } catch (err) {
      toast.error("Failed to delete bulletin");
    }
  };

  const toggleActive = async (bulletin) => {
    try {
      await axios.put(`${API}/bulletins/${bulletin.id}`, {
        ...bulletin,
        is_active: !bulletin.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulletins(bulletins.map(b => 
        b.id === bulletin.id ? { ...b, is_active: !b.is_active } : b
      ));
      toast.success(bulletin.is_active ? "Bulletin hidden" : "Bulletin visible");
    } catch (err) {
      toast.error("Failed to update bulletin");
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto" />
          <p className="text-zinc-400 mt-2">Loading bulletins...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-amber-400" />
            Bulletin Board
          </h3>
          <p className="text-sm text-zinc-400">
            Post announcements visible to clients when they book
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          disabled={bulletins.length >= maxBulletins}
          className="bg-amber-500 hover:bg-amber-600 text-black"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Bulletin
        </Button>
      </div>

      {/* Usage indicator */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-zinc-400">
          {bulletins.length}/{maxBulletins} bulletins used
        </span>
        {!isPro && bulletins.length >= 2 && (
          <Badge className="bg-amber-500/20 text-amber-400">
            Upgrade to Pro for more
          </Badge>
        )}
      </div>

      {/* Bulletins List */}
      {bulletins.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <Megaphone className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h4 className="text-white font-medium mb-2">No bulletins yet</h4>
            <p className="text-zinc-400 text-sm mb-4">
              Create your first bulletin to share announcements, sales, or special offers with clients
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Bulletin
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bulletins.map(bulletin => {
            const typeInfo = bulletinTypes.find(t => t.id === bulletin.type) || bulletinTypes[0];
            const isExpired = bulletin.end_date && new Date(bulletin.end_date) < new Date();
            
            return (
              <Card 
                key={bulletin.id} 
                className={`bg-zinc-900 border-zinc-800 ${!bulletin.is_active ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg ${typeInfo.color} flex items-center justify-center flex-shrink-0`}>
                        <typeInfo.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-white font-medium">{bulletin.title}</h4>
                          {bulletin.is_pinned && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Pinned
                            </Badge>
                          )}
                          {isExpired && (
                            <Badge className="bg-red-500/20 text-red-400 text-xs">Expired</Badge>
                          )}
                          {!bulletin.is_active && (
                            <Badge className="bg-zinc-700 text-zinc-400 text-xs">Hidden</Badge>
                          )}
                        </div>
                        <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{bulletin.message}</p>
                        
                        {/* Meta info */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          {bulletin.discount_code && (
                            <span className="flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              Code: {bulletin.discount_code}
                            </span>
                          )}
                          {bulletin.end_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Until {new Date(bulletin.end_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(bulletin)}
                        className="text-zinc-400 hover:text-white"
                      >
                        {bulletin.is_active ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingBulletin(bulletin)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(bulletin.id)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-400" />
              Create Bulletin
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Share announcements, sales, or special messages with your clients
            </DialogDescription>
          </DialogHeader>
          <BulletinForm 
            data={newBulletin} 
            setData={setNewBulletin} 
            onSubmit={handleCreate}
            onCancel={() => setShowCreateDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingBulletin} onOpenChange={() => setEditingBulletin(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-400" />
              Edit Bulletin
            </DialogTitle>
          </DialogHeader>
          {editingBulletin && (
            <BulletinForm 
              data={editingBulletin} 
              setData={setEditingBulletin} 
              onSubmit={handleUpdate}
              onCancel={() => setEditingBulletin(null)}
              saving={saving}
              isEdit
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulletinBoard;
